import React, { useState, useEffect } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import {
  FiPlus, FiGrid, FiSearch, FiEdit3, FiTrash2, FiChevronRight,
  FiUser, FiLayers, FiImage, FiX, FiInfo, FiActivity, FiSettings,
  FiArrowRight, FiCheckCircle, FiAlertCircle, FiMoreVertical,
  FiRefreshCw, FiPackage, FiZap
} from "react-icons/fi";

/* ─── CATEGORY ACCENT COLORS ─────────────────────────────────────────────── */
const ACCENTS = [
  { bg: "#EEF2FF", color: "#422AFB", border: "#C7D2FE", light: "#F5F7FF" },
  { bg: "#FEF3C7", color: "#B45309", border: "#FDE68A", light: "#FFFDF5" },
  { bg: "#ECFDF5", color: "#065F46", border: "#A7F3D0", light: "#F5FEFA" },
  { bg: "#FEE2E2", color: "#991B1B", border: "#FECACA", light: "#FFF5F5" },
  { bg: "#F0F9FF", color: "#0369A1", border: "#BAE6FD", light: "#F5FAFF" },
];
const accent = (name = "") => ACCENTS[(name.charCodeAt(0) || 0) % ACCENTS.length];

/* ─── MAIN COMPONENT ──────────────────────────────────────────────────────── */
const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [subSubmitting, setSubSubmitting] = useState(false);

  const [hodList, setHodList] = useState([]);
  const [formData, setFormData] = useState({ name: "", description: "", icon_image: null, hod_id: "" });
  const [subFormData, setSubFormData] = useState({ name: "", description: "" });

  useEffect(() => { fetchInitialData(); }, []);
  useEffect(() => { if (selectedCat) fetchSubCategories(selectedCat.id); }, [selectedCat]);

  const fetchInitialData = async () => {
    try {
      const [res, hodRes] = await Promise.all([
        AdminService.getAllCategories(),
        AdminService.getAllHods()
      ]);
      if (res.success) setCategories(res.data);
      if (hodRes.success) setHodList(hodRes.data);
    } catch (err) {
      console.error("Critical Sync Error:", err);
    } finally {
      setTimeout(() => setLoading(false), 600);
    }
  };

  const fetchSubCategories = async (id) => {
    try {
      const res = await AdminService.getSubCategories(id);
      if (res.success) setSubCategories(res.data);
    } catch (err) { console.error(err); }
  };

  const openCreateModal = () => {
    setIsEdit(false);
    setFormData({ name: "", description: "", icon_image: null, hod_id: "" });
    setShowModal(true);
  };

  const openEditModal = (cat) => {
    setIsEdit(true);
    // Find linked HOD id if available
    const linkedHod = hodList.find(h => h.category_id === cat.id);
    setFormData({ 
      name: cat.name, 
      description: cat.description, 
      icon_image: null, 
      hod_id: linkedHod ? linkedHod.id : "" 
    });
    setShowModal(true);
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new FormData();
    data.append("name", formData.name.trim());
    data.append("description", formData.description || "");
    if (formData.hod_id) data.append("hod_id", formData.hod_id);
    if (formData.icon_image) data.append("image", formData.icon_image);
    try {
      let res;
      if (isEdit) res = await AdminService.updateCategory(selectedCat.id, data);
      else res = await AdminService.createCategory(data);
      if (res.success) {
        setShowModal(false);
        fetchInitialData();
        if (isEdit) setSelectedCat(null);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Registry Error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm("All linked sub-categories and Concern Person mappings will be permanently deleted. Continue?")) {
      try {
        const res = await AdminService.deleteCategory(id);
        if (res.success) { setSelectedCat(null); fetchInitialData(); }
      } catch { alert("Deletion Interrupted"); }
    }
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    setSubSubmitting(true);
    try {
      let res;
      if (editingSub) res = await AdminService.updateSubCategory(editingSub.id, subFormData);
      else res = await AdminService.createSubCategory({ category_id: selectedCat.id, ...subFormData });
      if (res.success) {
        setShowSubModal(false);
        setEditingSub(null);
        setSubFormData({ name: "", description: "" });
        fetchSubCategories(selectedCat.id);
        fetchInitialData();
      }
    } catch (err) { alert(err.response?.data?.message); }
    finally { setSubSubmitting(false); }
  };

  const handleDeleteSub = async (subId) => {
    if (window.confirm("Remove this sub-category?")) {
      try {
        const res = await AdminService.deleteSubCategory(subId);
        if (res.success) { fetchSubCategories(selectedCat.id); fetchInitialData(); }
      } catch { alert("Delete failed"); }
    }
  };

  const filtered = categories.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalSubs = categories.reduce((a, b) => a + (b.sub_count || 0), 0);

  /* ── LOADER ────────────────────────────────────────────────────────────── */
  if (loading) return (
    <div style={s.loaderWrap}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}`}</style>
      <div style={s.loaderCard}>
        <div style={s.loaderRingWrap}>
          <div style={s.spinner} />
          <div style={s.loaderCore} />
        </div>
        <div style={s.loaderBrand}>ACHAL JEWELS</div>
        <div style={s.loaderSub}>Architecting Service Infrastructure...</div>
        <div style={s.loaderDots}>
          {[0,1,2].map(i=><span key={i} style={{...s.dot,animationDelay:`${i*0.18}s`}}/>)}
        </div>
      </div>
    </div>
  );

  /* ── MAIN ──────────────────────────────────────────────────────────────── */
  return (
    <div style={s.layout}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:.35}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slideRight{from{opacity:0;transform:translateX(18px)}to{opacity:1;transform:translateX(0)}}
        @keyframes modalPop{from{opacity:0;transform:scale(.96) translateY(8px)}to{opacity:1;transform:scale(1) translateY(0)}}
        .cat-card:hover{box-shadow:0 20px 50px rgba(27,37,89,0.09)!important;transform:translateY(-2px)}
        .cat-card{transition:transform .2s,box-shadow .2s}
        .sub-item:hover{background:#F1F5FF!important}
        .add-btn:hover{background:#2D3D7A!important;transform:translateY(-1px)}
        .refresh-btn:hover{background:#EEF2FF!important;color:#422AFB!important}
        .mini-plus:hover{background:#2D3D7A!important}
        .sub-edit:hover{color:#422AFB!important}
        .sub-del:hover{color:#EF4444!important}
        input:focus,select:focus,textarea:focus{border-color:#D4AF37!important;box-shadow:0 0 0 3px rgba(212,175,55,0.12)!important;outline:none}
      `}</style>

      <AdminSidebar />
      <div style={s.main}>

        {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
        <div style={s.pageHeader}>
          <div>
            <div style={s.breadcrumb}>
              <FiGrid size={10}/>
              <span style={{color:"#D4AF37",fontWeight:700}}>Terminal</span>
              <FiArrowRight size={9} style={{color:"#CBD5E1"}}/>
              <span>Service Directory</span>
            </div>
            <h1 style={s.pageTitle}>Categories Hub</h1>
            <p style={s.pageSubtitle}>Manage service categories, Sub-Categories, and department mappings.</p>
          </div>
          <div style={s.headerRight}>
            <button className="refresh-btn" style={s.refreshBtn} onClick={fetchInitialData} title="Refresh">
              <FiRefreshCw size={15}/>
            </button>
            <div style={s.searchWrap}>
              <FiSearch style={s.searchIcon} size={14}/>
              <input
                type="text" placeholder="Search categories..."
                style={s.searchInput} value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={()=>setSearchTerm("")} style={s.clearX}><FiX size={12}/></button>
              )}
            </div>
            <button className="add-btn" style={s.addBtn} onClick={openCreateModal}>
              <FiPlus size={16}/><span>New Category</span>
            </button>
          </div>
        </div>

        {/* ── STAT CARDS ──────────────────────────────────────────────────── */}
        <div style={s.statsRow}>
          <StatCard
            label="Active Categories" value={categories.length}
            sub="Operational service hubs"
            icon={<FiGrid size={19} color="#D4AF37"/>} iconBg="#FFFBEB" accent="#D4AF37"
          />
          <StatCard
            label="Total Sub-Categories" value={totalSubs}
            sub="Linked service protocols"
            icon={<FiLayers size={19} color="#422AFB"/>} iconBg="#EEF2FF" accent="#422AFB"
          />
          <StatCard
            label="Search Results" value={filtered.length}
            sub={`of ${categories.length} categories`}
            icon={<FiSearch size={19} color="#0369A1"/>} iconBg="#F0F9FF" accent="#0369A1"
          />
        </div>

        {/* ── MAIN CONTENT AREA ───────────────────────────────────────────── */}
        <div style={s.contentArea}>

          {/* LEFT: CATEGORY GRID */}
          <div style={{flex: selectedCat ? "0 0 58%" : "1 1 100%", minWidth:0, transition:"flex .3s ease"}}>

            {/* Panel header */}
            <div style={s.gridPanelHeader}>
              <div>
                <span style={s.panelTitle}>Service Categories</span>
                {searchTerm && (
                  <span style={s.filterNote}> · Filtering: "{searchTerm}"
                    <button onClick={()=>setSearchTerm("")} style={s.clearFilter}>Clear</button>
                  </span>
                )}
              </div>
              <span style={s.countBadge}>{filtered.length} total</span>
            </div>

            {/* Grid */}
            {filtered.length === 0 ? (
              <div style={s.emptyGrid}>
                <div style={s.emptyGridIcon}><FiGrid size={30} color="#CBD5E1"/></div>
                <div style={s.emptyGridTitle}>No categories found</div>
                <div style={s.emptyGridText}>
                  {searchTerm ? `No results for "${searchTerm}"` : "Click 'New Category' to get started."}
                </div>
              </div>
            ) : (
              <div style={{
                display:"grid",
                gridTemplateColumns: selectedCat ? "repeat(auto-fill,minmax(260px,1fr))" : "repeat(auto-fill,minmax(300px,1fr))",
                gap:18,
              }}>
                {filtered.map((cat, idx) => {
                  const ac = accent(cat.name);
                  const isSelected = selectedCat?.id === cat.id;
                  return (
                    <div
                      key={cat.id}
                      className="cat-card"
                      onClick={() => setSelectedCat(isSelected ? null : cat)}
                      style={{
                        ...s.catCard,
                        border: isSelected ? `2px solid #D4AF37` : "1.5px solid #F0F2FA",
                        background: isSelected ? "#FFFDF5" : "#FFF",
                        animation:`fadeUp .3s ease both`,
                        animationDelay:`${idx*.05}s`,
                      }}
                    >
                      {/* Card top row */}
                      <div style={s.cardTop}>
                        <div style={{...s.iconBox, background: ac.bg, border:`1.5px solid ${ac.border}`}}>
                          {cat.image ? (
                            <img src={`https://api.ajplservices.tech/${cat.image}`} alt="" style={s.catImg}/>
                          ) : (
                            <FiGrid size={22} color={ac.color}/>
                          )}
                        </div>
                        <div style={{...s.subBadge, background: ac.bg, color: ac.color, border:`1px solid ${ac.border}`}}>
                          <FiLayers size={10}/> {cat.sub_count || 0} Sub-Categories
                        </div>
                      </div>

                      {/* Name + description */}
                      <h3 style={s.catName}>{cat.name}</h3>
                      <p style={s.catDesc}>{cat.description || "General maintenance and operational support."}</p>

                      {/* Footer */}
                      <div style={s.cardFooter}>
                        <div style={s.hodTag}>
                          <div style={{...s.hodDot, background: ac.color}}/>
                          <FiUser size={11}/> {cat.hod_name || "Unassigned"}
                        </div>
                        <div style={{
                          ...s.viewBtn,
                          background: isSelected ? "#1B2559" : ac.bg,
                          color: isSelected ? "#FFF" : ac.color,
                          border: `1px solid ${isSelected ? "#1B2559" : ac.border}`,
                        }}>
                          {isSelected ? "Viewing" : "Open"} <FiChevronRight size={12}/>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT: DETAIL DRAWER */}
          {selectedCat && (() => {
            const ac = accent(selectedCat.name);
            return (
              <div style={s.drawer}>
                {/* Banner */}
                <div style={{...s.drawerBanner, background:`linear-gradient(135deg, #0F172A 0%, #1B2559 60%, ${ac.color}55 100%)`}}>
                  <div style={s.drawerBannerGlow}/>
                  <button style={s.closeBtn} onClick={()=>setSelectedCat(null)}><FiX size={14}/></button>
                  <span style={s.drawerTag}>CATEGORY DETAIL</span>
                </div>

                {/* Floating icon */}
                <div style={s.drawerIconWrap}>
                  <div style={{...s.drawerIconBox, background: ac.bg, border:`3px solid ${ac.border}`}}>
                    {selectedCat.image ? (
                      <img src={`https://api.ajplservices.tech/${selectedCat.image}`} alt="" style={s.drawerImg}/>
                    ) : (
                      <FiGrid size={30} color={ac.color}/>
                    )}
                  </div>
                </div>

                <div style={s.drawerBody}>
                  {/* Title */}
                  <div style={s.drawerTitleBlock}>
                    <h2 style={s.drawerCatName}>{selectedCat.name}</h2>
                    <p style={s.drawerCatDesc}>{selectedCat.description || "General operational support category."}</p>
                    <div style={s.hodBadge}>
                      <FiUser size={11} color="#0369A1"/>
                      <span>Managed by: <strong>{selectedCat.hod_name || "Unassigned"}</strong></span>
                    </div>
                  </div>

                  {/* Quick stats */}
                  <div style={s.drawerMiniStats}>
                    <div style={s.miniStat}>
                      <div style={{...s.miniStatIcon, background: ac.bg}}>
                        <FiLayers size={13} color={ac.color}/>
                      </div>
                      <div style={s.miniStatValue}>{selectedCat.sub_count || 0}</div>
                      <div style={s.miniStatLabel}>Sub-Categories</div>
                    </div>
                    <div style={s.miniStatDivider}/>
                    <div style={s.miniStat}>
                      <div style={{...s.miniStatIcon, background:"#ECFDF5"}}>
                        <FiActivity size={13} color="#10B981"/>
                      </div>
                      <div style={s.miniStatValue}>Active</div>
                      <div style={s.miniStatLabel}>Status</div>
                    </div>
                    <div style={s.miniStatDivider}/>
                    <div style={s.miniStat}>
                      <div style={{...s.miniStatIcon, background:"#FDF8E8"}}>
                        <FiZap size={13} color="#D4AF37"/>
                      </div>
                      <div style={s.miniStatValue}>{selectedCat.id}</div>
                      <div style={s.miniStatLabel}>Cat. ID</div>
                    </div>
                  </div>

                  {/* Sub-category list */}
                  <div style={s.subHeader}>
                    <div>
                      <div style={s.subHeaderTitle}>Task Breakdown</div>
                      <div style={s.subHeaderCount}>{subCategories.length} linked protocols</div>
                    </div>
                    <button
                      className="mini-plus"
                      style={s.miniPlus}
                      onClick={()=>{ setEditingSub(null); setSubFormData({name:"",description:""}); setShowSubModal(true); }}
                    >
                      <FiPlus size={12}/> New Sub-Categories
                    </button>
                  </div>

                  <div style={s.subList}>
                    {subCategories.length === 0 ? (
                      <div style={s.subEmpty}>
                        <FiPackage size={22} color="#CBD5E1" style={{marginBottom:6}}/>
                        <div>No Sub-Categories linked yet.</div>
                      </div>
                    ) : (
                      subCategories.map((sub, i) => (
                        <div key={sub.id} className="sub-item" style={{
                          ...s.subItem,
                          animation:`fadeUp .25s ease both`,
                          animationDelay:`${i*.04}s`,
                        }}>
                          <div style={s.subNum}>{i+1}</div>
                          <div style={s.subInfo}>
                            <div style={s.subName}>{sub.name}</div>
                            <div style={s.subMeta}>{sub.description || "Service Protocol"}</div>
                          </div>
                          <div style={s.subActs}>
                            <button
                              className="sub-edit"
                              style={s.subEditBtn}
                              onClick={()=>{ setEditingSub(sub); setSubFormData({name:sub.name,description:sub.description}); setShowSubModal(true); }}
                              title="Edit"
                            >
                              <FiEdit3 size={13}/>
                            </button>
                            <button
                              className="sub-del"
                              style={s.subDelBtn}
                              onClick={()=>handleDeleteSub(sub.id)}
                              title="Delete"
                            >
                              <FiTrash2 size={13}/>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Drawer actions */}
                  <div style={s.drawerActions}>
                    <button style={s.editBtn} onClick={()=>openEditModal(selectedCat)}>
                      <FiEdit3 size={14}/> Modify Hub
                    </button>
                    <button style={s.delBtn} onClick={()=>handleDeleteCategory(selectedCat.id)}>
                      <FiTrash2 size={14}/> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ── CATEGORY MODAL ──────────────────────────────────────────────── */}
        {showModal && (
          <div style={s.overlay} onClick={()=>setShowModal(false)}>
            <div style={s.modal} onClick={e=>e.stopPropagation()}>
              {/* Modal header */}
              <div style={s.modalHeader}>
                <div style={s.modalHeaderLeft}>
                  <div style={s.modalHeaderIcon}>
                    {isEdit ? <FiEdit3 size={17} color="#422AFB"/> : <FiPlus size={17} color="#422AFB"/>}
                  </div>
                  <div>
                    <h3 style={s.modalTitle}>{isEdit ? "Modify Hub" : "Initialize Service Hub"}</h3>
                    <p style={s.modalSub}>{isEdit ? "Update category details and scope." : "Create a new operational service category."}</p>
                  </div>
                </div>
                <button style={s.modalClose} onClick={()=>setShowModal(false)}><FiX size={15}/></button>
              </div>

              <form onSubmit={handleCategorySubmit}>
                <div style={s.modalBody}>
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiGrid size={12}/> Hub Name <span style={s.req}>*</span></label>
                    <input
                      style={s.fieldInput} required
                      placeholder="e.g. Electrical, Housekeeping..."
                      value={formData.name}
                      onChange={e=>setFormData({...formData,name:e.target.value})}
                    />
                  </div>
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiInfo size={12}/> Hub Scope / Description</label>
                    <textarea
                      style={s.fieldTextarea}
                      placeholder="Describe what this category covers..."
                      value={formData.description}
                      onChange={e=>setFormData({...formData,description:e.target.value})}
                    />
                  </div>
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiUser size={12}/> Assigned Concern Person (HOD)</label>
                    <select
                      style={{...s.fieldInput, height:42, cursor:"pointer"}}
                      value={formData.hod_id}
                      onChange={e=>setFormData({...formData, hod_id:e.target.value})}
                    >
                      <option value="">-- Select Concern Person (HOD) --</option>
                      {hodList.map(h => (
                        <option key={h.id} value={h.id}>
                          {h.full_name || h.username} ({h.department || "No Dept"})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiImage size={12}/> Identity Icon {isEdit && <span style={{color:"#A3AED0",fontWeight:500}}>(optional)</span>}</label>
                    <label htmlFor="cat-img" style={s.uploadZone}>
                      <FiImage size={20} color="#A3AED0"/>
                      <div style={s.uploadText}>
                        {formData.icon_image
                          ? <><span style={{color:"#1B2559",fontWeight:700}}>✓</span> {formData.icon_image.name}</>
                          : <><span style={{fontWeight:700,color:"#1B2559"}}>Click to upload</span> — PNG or JPG</>}
                      </div>
                      <input type="file" id="cat-img" style={{display:"none"}} accept="image/*"
                        onChange={e=>setFormData({...formData,icon_image:e.target.files[0]})}
                      />
                    </label>
                  </div>
                </div>
                <div style={s.modalFooter}>
                  <button type="button" style={s.cancelBtn} onClick={()=>setShowModal(false)}>Cancel</button>
                  <button type="submit" style={s.submitBtn} disabled={submitting}>
                    {submitting
                      ? <><div style={s.miniSpinner}/> Saving...</>
                      : <><FiCheckCircle size={14}/> {isEdit ? "Commit Changes" : "Activate Hub"}</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── SUB-CATEGORY MODAL ──────────────────────────────────────────── */}
        {showSubModal && (
          <div style={s.overlay} onClick={()=>setShowSubModal(false)}>
            <div style={{...s.modal, width:"min(520px,100%)"}} onClick={e=>e.stopPropagation()}>
              <div style={s.modalHeader}>
                <div style={s.modalHeaderLeft}>
                  <div style={s.modalHeaderIcon}>
                    {editingSub ? <FiEdit3 size={17} color="#422AFB"/> : <FiLayers size={17} color="#422AFB"/>}
                  </div>
                  <div>
                    <h3 style={s.modalTitle}>{editingSub ? "Modify Sub-Categories" : "Link New Sub-Categories"}</h3>
                    <p style={s.modalSub}>
                      {editingSub ? "Update protocol details." : `Adding to: ${selectedCat?.name}`}
                    </p>
                  </div>
                </div>
                <button style={s.modalClose} onClick={()=>setShowSubModal(false)}><FiX size={15}/></button>
              </div>
              <form onSubmit={handleSubSubmit}>
                <div style={s.modalBody}>
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiLayers size={12}/> Task Title <span style={s.req}>*</span></label>
                    <input
                      style={s.fieldInput} required
                      placeholder="e.g. Water Leakage, Fan Not Working..."
                      value={subFormData.name}
                      onChange={e=>setSubFormData({...subFormData,name:e.target.value})}
                    />
                  </div>
                  <div style={s.fieldGroup}>
                    <label style={s.fieldLabel}><FiInfo size={12}/> Protocol Note</label>
                    <input
                      style={s.fieldInput}
                      placeholder="Brief description of the service protocol..."
                      value={subFormData.description}
                      onChange={e=>setSubFormData({...subFormData,description:e.target.value})}
                    />
                  </div>
                </div>
                <div style={s.modalFooter}>
                  <button type="button" style={s.cancelBtn} onClick={()=>setShowSubModal(false)}>Cancel</button>
                  <button type="submit" style={s.submitBtn} disabled={subSubmitting}>
                    {subSubmitting
                      ? <><div style={s.miniSpinner}/> Saving...</>
                      : <><FiCheckCircle size={14}/> {editingSub ? "Update Task" : "Link Task"}</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

/* ─── STAT CARD ───────────────────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, icon, iconBg, accent: ac, isStatus }) => (
  <div style={s.statCard}>
    <div style={{...s.statIconBox, background: iconBg}}>{icon}</div>
    <div style={s.statBody}>
      <div style={s.statLabel}>{label}</div>
      <div style={{...s.statValue, color: isStatus ? "#10B981" : "#1B2559"}}>
        {isStatus ? (
          <span style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{width:8,height:8,borderRadius:"50%",background:"#10B981",display:"inline-block",boxShadow:"0 0 0 3px #D1FAE5"}}/>
            {value}
          </span>
        ) : value}
      </div>
      <div style={s.statSub}>{sub}</div>
    </div>
    <div style={{...s.statBar, background: ac}}/>
  </div>
);

/* ─── STYLES ──────────────────────────────────────────────────────────────── */
const s = {
  /* Layout */
  layout: { display:"flex", background:"#F8FAFC", minHeight:"100vh", fontFamily:"'Plus Jakarta Sans',sans-serif" },
  main: { marginLeft:280, padding:"32px 36px", flex:1, minWidth:0 },

  /* Loader */
  loaderWrap: { height:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#F8FAFC" },
  loaderCard: { display:"flex", flexDirection:"column", alignItems:"center", gap:12 },
  loaderRingWrap: { position:"relative", width:64, height:64, marginBottom:8 },
  spinner: { width:64, height:64, border:"3px solid #E2E8F0", borderTopColor:"#D4AF37", borderRadius:"50%", animation:"spin 1s linear infinite", position:"absolute" },
  loaderCore: { width:16, height:16, background:"#1B2559", borderRadius:"50%", position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)" },
  loaderBrand: { fontSize:15, fontWeight:900, color:"#1B2559", letterSpacing:6 },
  loaderSub: { fontSize:12, color:"#A3AED0", fontWeight:600 },
  loaderDots: { display:"flex", gap:6, marginTop:4 },
  dot: { width:6, height:6, borderRadius:"50%", background:"#D4AF37", animation:"pulse 1.2s ease infinite" },

  /* Page header */
  pageHeader: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:26, gap:20 },
  breadcrumb: { display:"flex", alignItems:"center", gap:6, fontSize:11, color:"#A3AED0", fontWeight:600, marginBottom:6, letterSpacing:.3 },
  pageTitle: { margin:0, fontSize:26, fontWeight:900, color:"#1B2559", letterSpacing:"-0.5px", lineHeight:1.2 },
  pageSubtitle: { margin:"4px 0 0", fontSize:13, color:"#A3AED0", fontWeight:500 },
  headerRight: { display:"flex", gap:10, alignItems:"center", flexShrink:0 },
  refreshBtn: { width:40, height:40, borderRadius:12, border:"1px solid #E9EDF7", background:"#FFF", color:"#A3AED0", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"0.2s" },
  searchWrap: { position:"relative", display:"flex", alignItems:"center" },
  searchIcon: { position:"absolute", left:12, color:"#A3AED0", pointerEvents:"none" },
  searchInput: { padding:"10px 36px 10px 36px", borderRadius:12, border:"1px solid #E9EDF7", width:230, outline:"none", background:"#FFF", fontSize:13, fontFamily:"inherit", color:"#1B2559", fontWeight:500, transition:"0.2s" },
  clearX: { position:"absolute", right:10, background:"none", border:"none", cursor:"pointer", color:"#A3AED0", display:"flex", padding:2 },
  addBtn: { background:"#1B2559", color:"#FFF", border:"none", padding:"10px 20px", borderRadius:12, fontWeight:700, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:7, transition:"0.2s", boxShadow:"0 8px 20px rgba(27,37,89,0.18)", flexShrink:0, fontFamily:"inherit" },

  /* Stats */
  statsRow: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:26 },
  statCard: { background:"#FFF", borderRadius:18, padding:"18px 20px", display:"flex", alignItems:"flex-start", gap:14, boxShadow:"0 2px 12px rgba(0,0,0,0.04)", border:"1px solid #F0F2FA", position:"relative", overflow:"hidden" },
  statIconBox: { width:44, height:44, borderRadius:13, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  statBody: { flex:1 },
  statLabel: { fontSize:11, color:"#A3AED0", fontWeight:700, textTransform:"uppercase", letterSpacing:.5, marginBottom:4 },
  statValue: { fontSize:22, fontWeight:900, lineHeight:1, marginBottom:3 },
  statSub: { fontSize:11, color:"#CBD5E1", fontWeight:500 },
  statBar: { position:"absolute", top:0, right:0, width:3, height:"100%", borderRadius:"0 18px 18px 0" },

  /* Content area */
  contentArea: { display:"flex", gap:20, alignItems:"flex-start" },

  /* Grid panel header */
  gridPanelHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 },
  panelTitle: { fontSize:14, fontWeight:800, color:"#1B2559" },
  filterNote: { fontSize:12, color:"#94A3B8", fontWeight:500 },
  clearFilter: { background:"none", border:"none", color:"#EE5D50", fontSize:12, fontWeight:700, cursor:"pointer", padding:"0 0 0 6px", fontFamily:"inherit" },
  countBadge: { background:"#EEF2FF", color:"#422AFB", padding:"4px 12px", borderRadius:8, fontSize:11, fontWeight:800 },

  /* Empty grid state */
  emptyGrid: { padding:"70px 20px", textAlign:"center", background:"#FFF", borderRadius:22, border:"1px solid #F0F2FA" },
  emptyGridIcon: { width:72, height:72, borderRadius:20, background:"#F8FAFC", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" },
  emptyGridTitle: { fontSize:16, fontWeight:800, color:"#1B2559", marginBottom:6 },
  emptyGridText: { fontSize:13, color:"#A3AED0", fontWeight:500 },

  /* Category cards */
  catCard: { background:"#FFF", borderRadius:22, padding:"22px", cursor:"pointer", boxShadow:"0 2px 12px rgba(0,0,0,0.04)" },
  cardTop: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16 },
  iconBox: { width:52, height:52, borderRadius:16, display:"flex", alignItems:"center", justifyContent:"center" },
  catImg: { width:36, height:36, objectFit:"contain" },
  subBadge: { display:"inline-flex", alignItems:"center", gap:4, padding:"5px 10px", borderRadius:10, fontSize:10, fontWeight:800, letterSpacing:.3 },
  catName: { fontSize:17, fontWeight:800, color:"#1B2559", margin:"0 0 8px" },
  catDesc: { fontSize:12, color:"#64748B", lineHeight:1.6, marginBottom:18, display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden" },
  cardFooter: { display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:14, borderTop:"1.5px solid #F8FAFC" },
  hodTag: { fontSize:12, color:"#1B2559", fontWeight:700, display:"flex", alignItems:"center", gap:6 },
  hodDot: { width:7, height:7, borderRadius:"50%" },
  viewBtn: { display:"inline-flex", alignItems:"center", gap:4, padding:"5px 12px", borderRadius:10, fontSize:11, fontWeight:800, cursor:"pointer" },

  /* Detail drawer */
  drawer: { width:340, flexShrink:0, background:"#FFF", borderRadius:22, border:"1px solid #F0F2FA", overflow:"hidden", position:"sticky", top:20, boxShadow:"0 8px 32px rgba(27,37,89,0.08)", animation:"slideRight .25s ease" },
  drawerBanner: { height:90, position:"relative", display:"flex", alignItems:"flex-end", padding:"0 18px 12px" },
  drawerBannerGlow: { position:"absolute", inset:0, backgroundImage:"radial-gradient(circle at 80% 20%, rgba(212,175,55,0.15) 0%, transparent 60%)", pointerEvents:"none" },
  closeBtn: { position:"absolute", top:12, right:12, width:28, height:28, borderRadius:"50%", background:"rgba(255,255,255,0.15)", border:"none", color:"#FFF", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  drawerTag: { fontSize:9, fontWeight:900, color:"rgba(255,255,255,0.45)", letterSpacing:2, textTransform:"uppercase" },

  drawerIconWrap: { display:"flex", justifyContent:"center", marginTop:-36, position:"relative", zIndex:1 },
  drawerIconBox: { width:80, height:80, borderRadius:22, background:"#F4F7FE", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 18px rgba(27,37,89,0.12)", overflow:"hidden" },
  drawerImg: { width:"100%", height:"100%", objectFit:"cover", borderRadius:19 },

  drawerBody: { padding:"14px 20px 22px" },
  drawerTitleBlock: { textAlign:"center", marginBottom:16 },
  drawerCatName: { margin:0, fontSize:18, fontWeight:900, color:"#1B2559", letterSpacing:"-0.3px" },
  drawerCatDesc: { margin:"6px 0 10px", fontSize:12, color:"#64748B", lineHeight:1.5 },
  hodBadge: { display:"inline-flex", alignItems:"center", gap:6, background:"#F0F9FF", padding:"6px 14px", borderRadius:20, fontSize:11, fontWeight:600, color:"#0369A1" },

  drawerMiniStats: { display:"flex", alignItems:"center", background:"#F8FAFC", borderRadius:14, padding:"12px 16px", marginBottom:18, gap:0 },
  miniStat: { flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 },
  miniStatIcon: { width:30, height:30, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center" },
  miniStatValue: { fontSize:15, fontWeight:900, color:"#1B2559", lineHeight:1 },
  miniStatLabel: { fontSize:9, color:"#A3AED0", fontWeight:700, textTransform:"uppercase", letterSpacing:.5 },
  miniStatDivider: { width:1, height:40, background:"#E9EDF7", margin:"0 8px" },

  subHeader: { display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:10 },
  subHeaderTitle: { fontSize:12, fontWeight:800, color:"#1B2559" },
  subHeaderCount: { fontSize:10, color:"#A3AED0", fontWeight:600, marginTop:2 },
  miniPlus: { display:"flex", alignItems:"center", gap:5, background:"#1B2559", color:"#FFF", border:"none", padding:"7px 13px", borderRadius:10, fontSize:11, fontWeight:800, cursor:"pointer", fontFamily:"inherit", transition:"0.2s" },

  subList: { display:"flex", flexDirection:"column", gap:8, maxHeight:260, overflowY:"auto", marginBottom:18, paddingRight:2 },
  subItem: { background:"#F8FAFC", borderRadius:14, padding:"12px 14px", display:"flex", alignItems:"center", gap:10, border:"1px solid #F0F2FA", transition:"background .15s" },
  subNum: { width:22, height:22, borderRadius:7, background:"#EEF2FF", color:"#422AFB", fontSize:10, fontWeight:900, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  subInfo: { flex:1, minWidth:0 },
  subName: { fontSize:13, fontWeight:800, color:"#1B2559", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  subMeta: { fontSize:10, color:"#94A3B8", marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  subActs: { display:"flex", gap:6, flexShrink:0 },
  subEditBtn: { width:28, height:28, borderRadius:8, border:"1px solid #E9EDF7", background:"#FFF", color:"#94A3B8", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"color .15s" },
  subDelBtn: { width:28, height:28, borderRadius:8, border:"1px solid #FEE2E2", background:"#FFF5F5", color:"#FECACA", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", transition:"color .15s" },
  subEmpty: { textAlign:"center", padding:"28px 16px", fontSize:12, color:"#94A3B8", fontWeight:600, background:"#F8FAFC", borderRadius:14, display:"flex", flexDirection:"column", alignItems:"center", gap:4 },

  drawerActions: { display:"flex", gap:10 },
  editBtn: { flex:1, padding:"12px", borderRadius:13, border:"1.5px solid #E2E8F0", background:"none", fontWeight:800, fontSize:12, color:"#1B2559", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"inherit", transition:"0.2s" },
  delBtn: { display:"flex", alignItems:"center", gap:6, background:"#FEF2F2", color:"#EF4444", border:"1.5px solid #FECACA", padding:"12px 16px", borderRadius:13, cursor:"pointer", fontWeight:800, fontSize:12, fontFamily:"inherit" },

  /* Modals */
  overlay: { position:"fixed", inset:0, background:"rgba(15,23,42,0.45)", backdropFilter:"blur(8px)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000, padding:16 },
  modal: { background:"#FFF", width:"min(480px,100%)", borderRadius:20, boxShadow:"0 20px 50px rgba(0,0,0,0.16)", animation:"modalPop .2s ease", overflow:"hidden", maxHeight:"90vh", display:"flex", flexDirection:"column" },
  modalHeader: { padding:"16px 22px 14px", borderBottom:"1px solid #F4F7FE", display:"flex", alignItems:"center", justifyContent:"space-between" },
  modalHeaderLeft: { display:"flex", alignItems:"center", gap:12 },
  modalHeaderIcon: { width:38, height:38, borderRadius:11, background:"#EEF2FF", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  modalTitle: { margin:0, fontSize:16, fontWeight:900, color:"#1B2559" },
  modalSub: { margin:"2px 0 0", fontSize:11, color:"#A3AED0", fontWeight:500 },
  modalClose: { width:28, height:28, borderRadius:"50%", background:"#F4F7FE", border:"none", color:"#A3AED0", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  modalBody: { padding:"16px 22px", display:"flex", flexDirection:"column", gap:12, overflowY:"auto" },
  modalFooter: { padding:"12px 22px 16px", borderTop:"1px solid #F4F7FE", display:"flex", justifyContent:"flex-end", gap:8 },

  fieldGroup: { display:"flex", flexDirection:"column", gap:5 },
  fieldLabel: { fontSize:11, color:"#475569", fontWeight:700, display:"flex", alignItems:"center", gap:4 },
  req: { color:"#EE5D50", fontSize:10 },
  fieldInput: { padding:"8px 12px", borderRadius:10, border:"1.5px solid #E9EDF7", outline:"none", fontSize:12, color:"#1B2559", fontFamily:"inherit", fontWeight:500, transition:"0.2s" },
  fieldTextarea: { padding:"8px 12px", borderRadius:10, border:"1.5px solid #E9EDF7", outline:"none", fontSize:12, color:"#1B2559", fontFamily:"inherit", fontWeight:500, minHeight:58, resize:"none", transition:"0.2s" },
  uploadZone: { display:"flex", alignItems:"center", justifyContent:"center", gap:8, border:"1.5px dashed #CBD5E1", borderRadius:10, padding:"8px 12px", cursor:"pointer", background:"#FAFBFF", transition:"border-color .2s" },
  uploadText: { fontSize:11, color:"#64748B", fontWeight:500 },

  cancelBtn: { padding:"8px 16px", borderRadius:9, border:"1px solid #E9EDF7", background:"#F4F7FE", color:"#1B2559", fontWeight:700, fontSize:12, cursor:"pointer", fontFamily:"inherit" },
  submitBtn: { padding:"8px 18px", borderRadius:9, border:"none", background:"#1B2559", color:"#FFF", fontWeight:800, fontSize:12, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6, boxShadow:"0 4px 14px rgba(27,37,89,0.18)", transition:"0.2s" },
  miniSpinner: { width:12, height:12, border:"2px solid rgba(255,255,255,0.3)", borderTopColor:"#FFF", borderRadius:"50%", animation:"spin .7s linear infinite" },
};

export default Categories;